import { Group, Modal, Select, Stack, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IConfirmWithMessageSelectOption } from 'components/modals/confirm-with-message/ConfirmWithMessageProvider';
import P1Regular from 'components/typography/P1Regular';
import panic from 'errors/panic';
import { ReactNode, useEffect, useState } from 'react';
import Button from 'components/Button';

/**
 * Parameters of the ConfirmWithMessageModal component.
 */
export interface ConfirmWithMessageModalProps {
  title?: string;
  message: ReactNode;
  opened: boolean;
  requiredReason?: boolean;
  showReasonField?: boolean;
  reasonLabel?: string;
  selectLabel?: string;
  selectPlaceholder?: string;
  selectOptions?: IConfirmWithMessageSelectOption[];
  requiredSelect?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onClose: () => void;
  onConfirmWithMessage: ((reason?: string, selection?: string) => void) | (() => Promise<void>);
}

interface FormValues {
  reason: string;
  selection: string;
}

/**
 * Modal used to confirm an action.
 */
export default function ConfirmWithMessageModal({
  title,
  message,
  opened,
  requiredReason = false,
  showReasonField = true,
  reasonLabel = 'Dôvod',
  selectLabel,
  selectPlaceholder,
  selectOptions = [],
  requiredSelect = false,
  primaryButtonText = 'Potvrdiť',
  secondaryButtonText = 'Zrušiť',
  onClose,
  onConfirmWithMessage,
}: ConfirmWithMessageModalProps) {
  const [clicked, setClicked] = useState(false);
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      reason: '',
      selection: '',
    },
    validate: {
      reason: (value) => {
        if (requiredReason && value === '') {
          return 'Pole je povinné.';
        }

        return null;
      },
      selection: (value) => {
        if (requiredSelect && value === '') {
          return 'Pole je povinné.';
        }

        return null;
      },
    },
  });

  /**
   * Submits the form.
   */
  async function submit({ reason, selection }: FormValues) {
    try {
      setLoading(true);
      setClicked(true);
      await onConfirmWithMessage(
        showReasonField ? (requiredReason ? reason : reason === '' ? undefined : reason) : undefined,
        selectOptions.length > 0 ? (selection === '' ? undefined : selection) : undefined
      );
      setLoading(false);
      form.reset();
      form.clearErrors();
      onClose();
    } catch (err: any) {
      panic(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      setClicked(false);
    }
  }, [opened]);

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title={title}
      zIndex={1000}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={24}>
          {typeof message === 'string' ? <P1Regular>{message}</P1Regular> : message}
          {selectOptions.length > 0 && (
            <Select
              label={selectLabel}
              placeholder={selectPlaceholder}
              data={selectOptions}
              size="md"
              disabled={loading}
              withinPortal
              zIndex={1100}
              dropdownPosition="bottom"
              {...form.getInputProps('selection')}
            />
          )}
          {showReasonField && (
            <Textarea
              label={reasonLabel}
              placeholder={`${reasonLabel} ${requiredReason ? '' : '(nepovinné)'}`}
              size="md"
              form="confirm-with-message-modal"
              disabled={loading}
              name="objekto-reason"
              data-autofocus
              {...form.getInputProps('reason')}
            />
          )}
          {!loading && !clicked && (
            <Group position="apart">
              <Button
                variant="subtle-gray"
                onClick={() => {
                  form.reset();
                  form.clearErrors();

                  onClose();
                }}
              >
                {secondaryButtonText}
              </Button>
              <Button variant="primary" type="submit">
                {primaryButtonText}
              </Button>
            </Group>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
